import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { formatDateUS } from "../lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Badge } from "./ui/badge";
import { Envelope, PaperPlaneTilt, Warning, CheckCircle, X } from "@phosphor-icons/react";
import { toast } from "sonner";

const SKIP_REASON_LABEL = {
    missing_debtor_address: "No return address on case",
    missing_creditor_address: "No creditor mailing address",
    no_recipient_email: "No recipient email — send an Offer first",
    credit_debit_failed: "Credit debit failed",
};

const POSTAGE_LABELS = {
    standard: "Standard first-class",
    certified: "USPS Certified",
    certified_receipt: "Certified + Return Receipt",
};

export default function SendPendingDialog({ open, onOpenChange, onComplete }) {
    const [channel, setChannel] = useState("usps");
    const [postageClass, setPostageClass] = useState("standard");
    const [preview, setPreview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [dispatching, setDispatching] = useState(false);
    const [testing, setTesting] = useState(false);
    const [result, setResult] = useState(null);

    useEffect(() => {
        if (!open) return;
        setResult(null);
        loadPreview();
    }, [open, channel, postageClass]);

    const loadPreview = async () => {
        setLoading(true);
        try {
            const payload = channel === "usps"
                ? { channel: "usps", postage_class: postageClass }
                : { channel: "email" };
            const { data } = await api.post("/vault/send-pending/preview", payload);
            setPreview(data);
        } catch (e) {
            toast.error(formatApiError(e));
            setPreview(null);
        } finally {
            setLoading(false);
        }
    };

    const sendTestToSelf = async () => {
        setTesting(true);
        try {
            const { data } = await api.post("/vault/send-pending/test");
            if (data.mocked) {
                toast.warning(`Preview logged server-side (RESEND_API_KEY missing) — inbox is unchanged.`);
            } else {
                toast.success(`Test preview sent to ${data.sent_to}`);
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || formatApiError(e));
        } finally {
            setTesting(false);
        }
    };


    const dispatch = async () => {
        if (!preview) return;
        const readyIds = preview.rows.filter((r) => r.status === "ready").map((r) => r.ai_output_id);
        if (readyIds.length === 0) return;
        setDispatching(true);
        try {
            const payload = {
                channel,
                postage_class: channel === "usps" ? postageClass : undefined,
                ai_output_ids: readyIds,
            };
            const { data } = await api.post("/vault/send-pending/dispatch", payload);
            setResult(data);
            const { sent, skipped, failed } = data.summary;
            if (sent > 0) {
                toast.success(`Dispatched ${sent} letter${sent === 1 ? "" : "s"}` +
                    (skipped || failed ? ` — ${skipped + failed} skipped/failed` : ""));
            } else {
                toast.error("Nothing was sent — see details in the dialog");
            }
            onComplete?.();
        } catch (e) {
            const detail = e?.response?.data?.detail || formatApiError(e);
            toast.error(detail);
        } finally {
            setDispatching(false);
        }
    };

    const close = () => {
        setResult(null);
        onOpenChange(false);
    };

    const totals = preview?.totals;
    const shortfallDollars = totals ? totals.credits_shortfall_cents / 100 : 0;
    const totalDollars = totals ? totals.total_cost_cents / 100 : 0;
    const balanceDollars = totals ? totals.credits_balance_cents / 100 : 0;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-3xl" data-testid="send-pending-dialog">
                <DialogHeader>
                    <DialogTitle className="font-serif text-2xl">
                        {result ? "Dispatch complete" : "Sign & send all pending"}
                    </DialogTitle>
                </DialogHeader>

                {!result && (
                    <>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                            <div>
                                <Label className="text-xs uppercase tracking-wider text-slate-500">Delivery channel</Label>
                                <RadioGroup value={channel} onValueChange={setChannel} className="mt-2 space-y-2" data-testid="send-channel">
                                    <label className={`flex items-start gap-3 p-3 rounded-md border cursor-pointer ${channel === "usps" ? "border-[#0F1E3D] bg-slate-50" : "border-slate-200"}`}>
                                        <RadioGroupItem value="usps" id="ch-usps" className="mt-0.5" data-testid="ch-usps" />
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2 font-medium text-slate-900">
                                                <PaperPlaneTilt size={14} weight="fill" /> Physical mail (USPS via Lob)
                                            </div>
                                            <p className="text-xs text-slate-500 mt-0.5">Uses your prepaid credit balance. Real postage & tracking.</p>
                                        </div>
                                    </label>
                                    <label className={`flex items-start gap-3 p-3 rounded-md border cursor-pointer ${channel === "email" ? "border-[#0F1E3D] bg-slate-50" : "border-slate-200"}`}>
                                        <RadioGroupItem value="email" id="ch-email" className="mt-0.5" data-testid="ch-email" />
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2 font-medium text-slate-900">
                                                <Envelope size={14} weight="fill" /> Email (Resend)
                                            </div>
                                            <p className="text-xs text-slate-500 mt-0.5">Attaches the signed PDF. Sends to the recipient of the most recent Offer on each case.</p>
                                        </div>
                                    </label>
                                </RadioGroup>
                            </div>

                            {channel === "usps" && (
                                <div>
                                    <Label className="text-xs uppercase tracking-wider text-slate-500">Postage class</Label>
                                    <Select value={postageClass} onValueChange={setPostageClass}>
                                        <SelectTrigger className="mt-2" data-testid="postage-class-select">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="standard">Standard first-class (~$2.30)</SelectItem>
                                            <SelectItem value="certified">USPS Certified (~$6.25)</SelectItem>
                                            <SelectItem value="certified_receipt">Certified + Return Receipt (~$9.05)</SelectItem>
                                        </SelectContent>
                                    </Select>
                                    <p className="text-xs text-slate-500 mt-2">
                                        Certified + Return Receipt is recommended for validation letters.
                                    </p>
                                </div>
                            )}
                        </div>

                        <div className="mt-4 border border-slate-200 rounded-md overflow-hidden" data-testid="preflight-table">
                            <table className="w-full text-sm">
                                <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
                                    <tr>
                                        <th className="text-left px-3 py-2 font-medium">Case</th>
                                        <th className="text-left px-3 py-2 font-medium">Letter</th>
                                        <th className="text-left px-3 py-2 font-medium">{channel === "usps" ? "Mail to" : "Email to"}</th>
                                        <th className="text-right px-3 py-2 font-medium">Cost</th>
                                        <th className="text-left px-3 py-2 font-medium">Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading && (
                                        <tr><td colSpan={5} className="px-3 py-4 text-slate-500 text-center">Loading preview…</td></tr>
                                    )}
                                    {!loading && preview?.rows?.length === 0 && (
                                        <tr><td colSpan={5} className="px-3 py-4 text-slate-500 text-center">No pending letters.</td></tr>
                                    )}
                                    {!loading && preview?.rows?.map((r) => (
                                        <tr key={r.ai_output_id} className="border-t border-slate-200" data-testid={`preflight-row-${r.ai_output_id}`}>
                                            <td className="px-3 py-2">
                                                <Link to={`/cases/${r.case_id}`} className="font-medium text-slate-800 hover:underline">
                                                    {r.case_title}
                                                </Link>
                                                <p className="text-xs text-slate-500">{formatDateUS(r.created_at)}</p>
                                            </td>
                                            <td className="px-3 py-2 text-slate-700">{r.mode_title}</td>
                                            <td className="px-3 py-2 text-slate-600 max-w-[220px] truncate" title={r.target}>{r.target}</td>
                                            <td className="px-3 py-2 text-right font-mono">
                                                {r.status === "ready" && r.cost_cents > 0 ? `$${(r.cost_cents / 100).toFixed(2)}` : "—"}
                                            </td>
                                            <td className="px-3 py-2">
                                                {r.status === "ready" ? (
                                                    <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]">Ready</Badge>
                                                ) : (
                                                    <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-[10px] gap-1">
                                                        <Warning size={10} /> {SKIP_REASON_LABEL[r.reason] || r.reason}
                                                    </Badge>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {totals && (
                            <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm" data-testid="preflight-totals">
                                <TotalStat label="Ready to send" value={totals.ready_count} tint="emerald" />
                                <TotalStat label="Will skip" value={totals.skip_count} tint="amber" />
                                {channel === "usps" && (
                                    <>
                                        <TotalStat label="Batch cost" value={`$${totalDollars.toFixed(2)}`} tint="slate" />
                                        <TotalStat label="Credit balance" value={`$${balanceDollars.toFixed(2)}`} tint={shortfallDollars > 0 ? "rose" : "slate"} />
                                    </>
                                )}
                            </div>
                        )}

                        {channel === "usps" && shortfallDollars > 0 && (
                            <div className="mt-3 p-3 rounded-md bg-rose-50 border border-rose-200 text-sm text-rose-800 flex items-center gap-2" data-testid="shortfall-warning">
                                <Warning size={14} weight="fill" />
                                Insufficient credits. You need <strong>${shortfallDollars.toFixed(2)}</strong> more.{" "}
                                <Link to="/credits" className="underline font-medium">Top up →</Link>
                            </div>
                        )}
                    </>
                )}

                {result && (
                    <div className="pt-2 space-y-4" data-testid="dispatch-result">
                        <div className="grid grid-cols-3 gap-3 text-sm">
                            <TotalStat label="Sent" value={result.summary.sent} tint="emerald" />
                            <TotalStat label="Skipped" value={result.summary.skipped} tint="amber" />
                            <TotalStat label="Failed" value={result.summary.failed} tint="rose" />
                        </div>
                        {result.summary.total_charged_cents > 0 && (
                            <p className="text-sm text-slate-600">
                                Charged <strong>${(result.summary.total_charged_cents / 100).toFixed(2)}</strong> from credits.
                                New balance: <strong>${(result.summary.new_balance_cents / 100).toFixed(2)}</strong>.
                            </p>
                        )}
                        <div className="border border-slate-200 rounded-md overflow-hidden max-h-64 overflow-y-auto">
                            <table className="w-full text-xs">
                                <thead className="bg-slate-50 text-slate-500 uppercase tracking-wider">
                                    <tr>
                                        <th className="text-left px-3 py-2 font-medium">Case</th>
                                        <th className="text-left px-3 py-2 font-medium">Status</th>
                                        <th className="text-left px-3 py-2 font-medium">Detail</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {result.results.map((r) => (
                                        <tr key={r.ai_output_id} className="border-t border-slate-200">
                                            <td className="px-3 py-2 text-slate-800">{r.case_title || "—"}</td>
                                            <td className="px-3 py-2">
                                                {r.status === "sent" ? (
                                                    <span className="inline-flex items-center gap-1 text-emerald-700">
                                                        <CheckCircle size={11} weight="fill" /> Sent
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center gap-1 text-amber-700">
                                                        <X size={11} weight="fill" /> {r.status}
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-3 py-2 text-slate-600 truncate max-w-[300px]">
                                                {r.status === "sent" && r.tracking_number && `Tracking: ${r.tracking_number}`}
                                                {r.status === "sent" && r.recipient && `→ ${r.recipient}`}
                                                {r.status === "sent" && r.mocked && <span className="text-amber-700 ml-1">(email MOCKED — RESEND_API_KEY missing)</span>}
                                                {r.status !== "sent" && (r.reason || "")}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                <DialogFooter className="mt-4 flex-col sm:flex-row sm:justify-between gap-2 sm:gap-3">
                    {!result ? (
                        <>
                            <Button
                                variant="ghost"
                                onClick={sendTestToSelf}
                                disabled={testing || loading || !preview?.rows?.length}
                                className="text-slate-600 hover:text-[#0F1E3D]"
                                data-testid="send-test-btn"
                                title="Emails a signed sample of the first pending letter to your own inbox"
                            >
                                <Envelope size={14} className="mr-1.5" />
                                {testing ? "Sending test…" : "Send test to myself"}
                            </Button>
                            <div className="flex items-center gap-2">
                                <Button variant="outline" onClick={close} data-testid="send-cancel">Cancel</Button>
                                <Button
                                    onClick={dispatch}
                                    disabled={dispatching || loading || !preview?.totals?.ready_count || (channel === "usps" && shortfallDollars > 0)}
                                    className="bg-[#0F1E3D] hover:bg-[#1A2C57]"
                                    data-testid="send-dispatch-btn"
                                >
                                    {dispatching
                                        ? "Sending…"
                                        : channel === "usps"
                                            ? `Sign & mail ${totals?.ready_count || 0} · $${totalDollars.toFixed(2)}`
                                            : `Sign & email ${totals?.ready_count || 0}`}
                                </Button>
                            </div>
                        </>
                    ) : (
                        <Button onClick={close} className="bg-[#0F1E3D] hover:bg-[#1A2C57] ml-auto" data-testid="send-done">Done</Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function TotalStat({ label, value, tint }) {
    const tones = {
        emerald: "bg-emerald-50 text-emerald-800 border-emerald-200",
        amber: "bg-amber-50 text-amber-800 border-amber-200",
        rose: "bg-rose-50 text-rose-800 border-rose-200",
        slate: "bg-slate-50 text-slate-800 border-slate-200",
    };
    return (
        <div className={`p-3 rounded-md border ${tones[tint] || tones.slate}`}>
            <p className="overline text-[10px]">{label}</p>
            <p className="font-mono text-lg font-semibold mt-1">{value}</p>
        </div>
    );
}
